// Agent coverage-area entitlement model.
//
// An agent may define several coverage areas (circles) for seller
// representation matching. The first 3 areas (oldest first, by creation order)
// are FREE. Every area beyond the free allotment is a paid add-on at a flat
// monthly price; a paid area only counts for lead matching while it is funded
// by a current Stripe add-on subscription item.
//
// Source of truth for whether a *specific* extra area is funded is the
// per-area `active` flag (see agent_service_areas.active):
//   - Areas within the free allotment (index < freeLimit) are ALWAYS active,
//     regardless of the flag.
//   - Areas beyond the free allotment are active only when `active === true`
//     (explicitly funded). A null/false flag means "payment required".
//
// This file is pure (no DB/Stripe) so it can be shared by the server route
// layer, the webhook reconciler, and the serializer without duplicating the
// rules.

// Number of coverage areas an agent gets for free (the oldest N by creation).
export const COVERAGE_AREA_FREE_LIMIT = 3;

// Flat monthly price (USD) of one paid coverage-area add-on unit.
export const COVERAGE_AREA_ADDON_MONTHLY_USD = 19;

export type CoverageAreaStatus = "free" | "paid_active" | "payment_required";

// Minimal per-area shape the evaluator needs. `active` is the stored funding
// flag (nullable). Areas MUST be passed oldest-first (creation order).
export interface CoverageAreaInput {
  id: number;
  active: boolean | null;
}

export interface CoverageAreaEvaluation {
  id: number;
  status: CoverageAreaStatus;
  // Whether this area currently counts for lead matching.
  active: boolean;
}

export interface CoverageAreaSummary {
  freeLimit: number;
  // How many of the free slots are in use (min(areaCount, freeLimit)).
  freeUsed: number;
  // Paid extras that are currently funded & active.
  activePaidCount: number;
  // Extras that exist but are not funded (require payment to activate).
  paymentRequiredCount: number;
  // Per-unit monthly price of the add-on.
  addonMonthlyUsd: number;
  // Current recurring monthly add-on charge (activePaidCount * addonMonthlyUsd).
  monthlyAddonTotalUsd: number;
}

// Evaluate an agent's coverage areas (oldest-first) into per-area statuses plus
// a billing summary. Pure: the same rules drive routes, webhook, and serializer.
export function evaluateCoverageAreas(
  areas: CoverageAreaInput[],
): { areas: CoverageAreaEvaluation[]; summary: CoverageAreaSummary } {
  const evaluated: CoverageAreaEvaluation[] = areas.map((area, index) => {
    if (index < COVERAGE_AREA_FREE_LIMIT) {
      return { id: area.id, status: "free", active: true };
    }
    const funded = area.active === true;
    return {
      id: area.id,
      status: funded ? "paid_active" : "payment_required",
      active: funded,
    };
  });

  const activePaidCount = evaluated.filter(
    (e) => e.status === "paid_active",
  ).length;
  const paymentRequiredCount = evaluated.filter(
    (e) => e.status === "payment_required",
  ).length;

  return {
    areas: evaluated,
    summary: {
      freeLimit: COVERAGE_AREA_FREE_LIMIT,
      freeUsed: Math.min(areas.length, COVERAGE_AREA_FREE_LIMIT),
      activePaidCount,
      paymentRequiredCount,
      addonMonthlyUsd: COVERAGE_AREA_ADDON_MONTHLY_USD,
      monthlyAddonTotalUsd: activePaidCount * COVERAGE_AREA_ADDON_MONTHLY_USD,
    },
  };
}

// The Stripe add-on quantity that should be billed for a given set of areas:
// the number of extras (beyond the free allotment) whose funding flag is true.
export function desiredCoverageAddonQuantity(
  areas: CoverageAreaInput[],
): number {
  return areas
    .slice(COVERAGE_AREA_FREE_LIMIT)
    .filter((a) => a.active === true).length;
}
